import { AnimatePresence, motion, useAnimation } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import PageDetails from "./PageDetails";
import { mainLinksNav, variantsNavMobile } from "./nav-constants";
import Close from "../../SVGs/nav-menu/Close";
import Hamburguer from "../../SVGs/nav-menu/Hamburguer";
import ArrowRight from "../../SVGs/nav-menu/ArrowRight";
import {
  NavMobileType,
  PrimaryNavBuilderType,
} from "src/assets/Types/Redesign/PrimaryNavMenuType.type";
import { ArrayElementType } from "@types";
import { CtaButton } from "@components/RedesignComponents/Cta/cta-button";
import SpLogo from "@components/RedesignComponents/SVGs/SpLogo";
import { useNavMobile } from "@sp-state/useNavMobile/useNavMobile";

const NavMobile: React.FC<PrimaryNavBuilderType> = ({
  onClickHandler,
  hiddenMobile = false,
}) => {
  const open = useNavMobile(state => state.open);
  const setOpen = useNavMobile(state => state.setOpen);

  const [activeItem, setActiveItem] = useState<NavMobileType>(null);
  const [showContent, setShowContent] = useState(false);
  const [isFirstItem, setIsFirstItem] = useState(false);
  const [distanceTop, setDistanceTop] = useState(0);
  const navWrapperRef = useRef<HTMLDivElement>(null);

  const animateBottomButtons = useAnimation();

  const handleBackContent = () => {
    setShowContent(false);
    setActiveItem(null);
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      document.body.style.overflow = "visible";
      setOpen(false);
    }
  }, []);

  const handleOpenMenu = async () => {
    if (!open) {
      setTimeout(() => {
        animateBottomButtons.start({
          opacity: 1,
        });
      }, 200);
      document.body.style.overflow = "hidden";
    } else {
      await animateBottomButtons.start({
        opacity: 0,
        transition: {
          duration: 0.1,
        },
      });
      document.body.style.overflow = "visible";
    }

    setOpen(!open);
    setShowContent(false);
    setActiveItem(null);
  };

  const onSelectSubMenu = (
    key: number,
    item: ArrayElementType<typeof mainLinksNav>
  ) => {
    setIsFirstItem(Boolean(key === 0));
    setShowContent(true);
    setTimeout(() => {
      setActiveItem(item.value);
    }, 5);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!navWrapperRef?.current) return 0;

      setDistanceTop(navWrapperRef.current.getBoundingClientRect().top + 56);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <>
      <div
        className={`px-10 py-4 h-[56px] sticky top-0 z-[999] hidden border-solid items-center justify-between border-b border-b-forest-100 md:flex sm:px-5 sm:py-3 bg-white container-primary-nav`}
        ref={navWrapperRef}
      >
        <a href="/" onClick={onClickHandler}>
          <SpLogo className="!w-[126px] h-[23.6px]" />
        </a>
        <button
          type="button"
          className="border-none bg-transparent outline-none"
          onClick={() => handleOpenMenu()}
        >
          {open ? <Close /> : <Hamburguer />}
        </button>
      </div>
      <AnimatePresence exitBeforeEnter>
        {open && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={{
              hidden: { height: 0 },
              visible: { height: `calc(100% - ${distanceTop}px)` },
            }}
            transition={{
              duration: 0.2,
            }}
            className={`bg-white fixed w-full z-[999999] overflow-y-auto overflow-x-hidden container-primary-nav`}
            style={{ top: distanceTop }}
          >
            <motion.div className="h-full px-10 py-12 sm:px-5 sm:py-12">
              <motion.div
                key="content1"
                variants={variantsNavMobile.mainContent}
                animate={showContent ? "hidden" : "visible"}
                transition={{ duration: 0.5 }}
                className="w-full h-full flex justify-center items-center"
              >
                <div className="flex flex-col h-full w-full">
                  <div className="flex flex-col gap-8 flex-1 sm:gap-6 header3">
                    {mainLinksNav.map((item, key) => {
                      if (item?.extraLink) {
                        return (
                          <a
                            key={key}
                            className="w-full flex items-center justify-between no-underline button-no-styling"
                            onClick={onClickHandler}
                            href={item.extraLink}
                          >
                            <h3>{item.label}</h3>
                          </a>
                        );
                      }
                      return (
                        <div
                          key={key}
                          className="w-full flex items-center justify-between"
                          onClick={() => onSelectSubMenu(key, item)}
                        >
                          <h3>{item.label}</h3>
                          <button
                            type="button"
                            className="border-none bg-transparent"
                          >
                            <ArrowRight />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <motion.div
                    initial={{
                      opacity: 0,
                    }}
                    animate={animateBottomButtons}
                    className="flex justify-center flex-col gap-8 items-center sm:gap-6"
                  >
                    <CtaButton
                      copy="Start free trial"
                      copySize="bodyRegular"
                      isDisabled={false}
                      link="/sign-up/"
                      linkUrl={null}
                      variant="primarySun"
                      className="w-full no-underline button-no-styling"
                      onClick={e =>
                        onClickHandler(e, null, "Marketing CTA Clicked")
                      }
                    />
                    <div className="flex">
                      <CtaButton
                        copy="Sign in"
                        copySize="bodyRegular"
                        isDisabled={false}
                        linkUrl="https://secure.simplepractice.com/users/sign_in"
                        link={null}
                        variant="tertiaryForest"
                        onClick={e =>
                          onClickHandler(e, {
                            category: "log in",
                          })
                        }
                      />
                    </div>
                  </motion.div>
                </div>
              </motion.div>
              <AnimatePresence mode="wait">
                <motion.div
                  variants={variantsNavMobile.secondContent}
                  initial="hidden"
                  animate={showContent ? "visible" : "hidden"}
                  className="absolute top-0 w-full flex justify-center items-center h-auto"
                >
                  <div className="py-8 w-full sm:py-6 sm:pr-10">
                    <PageDetails
                      data={activeItem}
                      handleBackContent={handleBackContent}
                      onClick={onClickHandler}
                      isFirstItem={isFirstItem}
                    />
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavMobile;
